/**
 * The display preferences as data (no DOM, no store). Since plan 9b (spec 2026-10-02
 * upacara-bersama-dan-akun 5.4) they follow the account: the server holds each person's, and
 * `simu:tampilan` holds this device's copy of each person's plus who signed in last, whose copy
 * index.html applies before React mounts. A device from before holds one flat choice
 * (`warisan`) until someone comes online (./preferensi), and keeps what it showed then
 * (`sebelum`) for every account without a choice of its own: no one's display changes at the
 * update. `device.ts` binds the attributes to the browser; `test/tampilan.test.ts` runs
 * index.html's own script against this module.
 */

export type Kepadatan = 'rapat' | 'biasa'
export type UkuranTeks = 'kecil' | 'sedang' | 'besar'
/** `system` follows the OS preference; `reduced` forces `html[data-motion="reduced"]`. */
export type PilihanGerakan = 'system' | 'reduced'

// Legacy storage name from SIMU; kept so devices keep their data (pinned: test/nama-warisan.test.ts).
export const TAMPILAN_KEY = 'simu:tampilan'
export const KEPADATAN: readonly Kepadatan[] = ['rapat', 'biasa']
export const UKURAN_TEKS: readonly UkuranTeks[] = ['kecil', 'sedang', 'besar']

/** What the key stores; every field optional so an older or hand-edited value still reads. */
export interface TampilanTersimpan {
  density?: Kepadatan
  text?: UkuranTeks
  motion?: PilihanGerakan
}

export interface Tampilan {
  density: Kepadatan
  text: UkuranTeks
  motion: PilihanGerakan
}

export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/** `localStorage`, or null where it is missing or blocked (the one accessor of the store). */
export function browserStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export interface RootLike {
  dataset: { density?: string | undefined; text?: string | undefined; motion?: string | undefined }
}

export function isKepadatan(value: unknown): value is Kepadatan {
  return value === 'rapat' || value === 'biasa'
}

export function isUkuranTeks(value: unknown): value is UkuranTeks {
  return value === 'kecil' || value === 'sedang' || value === 'besar'
}

/** What `simu:tampilan` says: each person's copy, who signed in last, an older device choice. */
export interface SalinanTampilan {
  /** The person whose copy styles the page before React loads; null when nobody's is known. */
  terakhir: string | null
  orang: Record<string, TampilanTersimpan>
  /** A device from before plan 9b: its one flat choice, until someone comes online. */
  warisan: TampilanTersimpan | null
  /**
   * What a device from before plan 9b showed: its flat choice, or `{}` for its own defaults.
   * Every account without a choice of its own keeps it on this device. null on a device with
   * nothing from before, where the parish defaults apply.
   */
  sebelum: TampilanTersimpan | null
}

/** The parish's defaults (Pengaturan paroki): density and text size, no motion. */
export interface BawaanParoki {
  density: Kepadatan
  text: UkuranTeks
}

const objek = (raw: unknown): raw is Record<string, unknown> =>
  typeof raw === 'object' && raw !== null && !Array.isArray(raw)

/** One stored preference, tolerant of anything that is not the expected shape. */
export function tampilanDari(raw: unknown): TampilanTersimpan {
  if (!objek(raw)) return {}
  const out: TampilanTersimpan = {}
  if (isKepadatan(raw.density)) out.density = raw.density
  if (isUkuranTeks(raw.text)) out.text = raw.text
  if (raw.motion === 'reduced' || raw.motion === 'system') out.motion = raw.motion
  return out
}

const kosong = (t: TampilanTersimpan) => Object.keys(t).length === 0

/** `simu:tampilan` as it stands, whatever it holds (the v2 shape, a flat older value, junk). */
export function bacaSalinan(storage: StorageLike | null): SalinanTampilan {
  const tiada: SalinanTampilan = { terakhir: null, orang: {}, warisan: null, sebelum: null }
  if (storage === null) return tiada
  let raw: unknown
  try {
    raw = JSON.parse(storage.getItem(TAMPILAN_KEY) ?? 'null')
  } catch {
    return tiada
  }
  if (!objek(raw)) return tiada
  if (raw.v !== 2) {
    // Only an app from before plan 9b wrote this shape: the device showed it.
    const warisan = tampilanDari(raw)
    return {
      terakhir: null,
      orang: {},
      warisan: kosong(warisan) ? null : warisan,
      sebelum: warisan,
    }
  }
  const orang: Record<string, TampilanTersimpan> = {}
  if (objek(raw.orang)) {
    for (const [id, nilai] of Object.entries(raw.orang)) {
      const t = tampilanDari(nilai)
      if (id !== '' && id !== '__proto__' && !kosong(t)) orang[id] = t
    }
  }
  const terakhir =
    typeof raw.terakhir === 'string' && Object.hasOwn(orang, raw.terakhir) ? raw.terakhir : null
  return {
    terakhir,
    orang,
    warisan: null,
    sebelum: objek(raw.sebelum) ? tampilanDari(raw.sebelum) : null,
  }
}

/**
 * Writes the v2 shape; an older device choice is not written back (it is used up), what the
 * device showed before is.
 */
export function tulisSalinan(storage: StorageLike | null, salinan: SalinanTampilan): void {
  if (storage === null) return
  try {
    storage.setItem(
      TAMPILAN_KEY,
      JSON.stringify({
        v: 2,
        terakhir: salinan.terakhir,
        orang: salinan.orang,
        ...(salinan.sebelum === null ? {} : { sebelum: salinan.sebelum }),
      }),
    )
  } catch {
    // Storage full or blocked: the attributes still apply for this session.
  }
}

/**
 * This person's copy; the older device choice is used up. It becomes the one index.html
 * applies only while this person is on screen (`diLayar`): an answer that lands after the
 * screen passed to someone else never styles the next PIN screen.
 */
export function denganSalinan(
  salinan: SalinanTampilan,
  userId: string,
  pref: Tampilan,
  diLayar = true,
): SalinanTampilan {
  return {
    terakhir: diLayar ? userId : salinan.terakhir,
    orang: { ...salinan.orang, [userId]: pref },
    warisan: null,
    sebelum: salinan.sebelum,
  }
}

/**
 * At start, before anyone signs in: a device SATU was signed in on before plan 9b
 * (`pernahMasuk`, its parish cache) that holds no display choice showed its own defaults.
 * They are written down, so that every account without its own keeps them here. A device
 * never signed in on gets nothing: the parish defaults apply there.
 */
export function catatTampilanSebelum(storage: StorageLike | null, pernahMasuk: boolean): void {
  if (storage === null || !pernahMasuk) return
  try {
    if (storage.getItem(TAMPILAN_KEY) !== null) return
  } catch {
    return
  }
  tulisSalinan(storage, { terakhir: null, orang: {}, warisan: null, sebelum: {} })
}

/**
 * What styles the page before React mounts: the last person's copy, else what the device
 * showed before.
 */
export function tampilanTersimpanAwal(salinan: SalinanTampilan): TampilanTersimpan {
  if (salinan.terakhir !== null) return salinan.orang[salinan.terakhir] ?? {}
  return salinan.sebelum ?? {}
}

/** The density index.html picks when nothing is stored: biasa on coarse or narrow devices. */
export function kepadatanBawaan(coarseOrNarrow: boolean): Kepadatan {
  return coarseOrNarrow ? 'biasa' : 'rapat'
}

/** The device's own defaults, as index.html's: its class's density, sedang, the OS's motion. */
export function tampilanPerangkat(coarseOrNarrow: boolean): Tampilan {
  return { density: kepadatanBawaan(coarseOrNarrow), text: 'sedang', motion: 'system' }
}

/** Spec 5.4: the person's own, else the parish defaults, else the device's, field by field. */
export function pilihTampilan(
  milik: TampilanTersimpan | null,
  paroki: BawaanParoki | null,
  perangkat: Tampilan,
): Tampilan {
  return {
    density: milik?.density ?? paroki?.density ?? perangkat.density,
    text: milik?.text ?? paroki?.text ?? perangkat.text,
    motion: milik?.motion ?? perangkat.motion,
  }
}

/**
 * Writes the three attributes exactly as index.html does before React mounts, so a
 * change here and a reload land on the same first paint.
 */
export function terapkanTampilan(root: RootLike, pref: Tampilan, osReduce: boolean): void {
  root.dataset.density = pref.density
  root.dataset.text = pref.text
  root.dataset.motion = pref.motion === 'reduced' || osReduce ? 'reduced' : 'full'
}

/** The effective preference at start: the stored copy, else what index.html applied. */
export function tampilanAwal(storage: StorageLike | null, root: RootLike | null): Tampilan {
  const stored = tampilanTersimpanAwal(bacaSalinan(storage))
  const density = isKepadatan(root?.dataset.density) ? root.dataset.density : 'biasa'
  const text = isUkuranTeks(root?.dataset.text) ? root.dataset.text : 'sedang'
  return {
    density: stored.density ?? density,
    text: stored.text ?? text,
    motion: stored.motion ?? 'system',
  }
}
